'use client';

import { OnboardingStep } from '@ctrl-enter/types';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('web:component:onboarding-flow');
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Building2, Sparkles, User, X } from 'lucide-react';
import * as React from 'react';
import {
  gatewayQueryKeys,
  useOrgPersonalizationQuery,
  useUpdateOrgPersonalizationMutation,
  useUpdateUserPersonalizationMutation,
} from '../hooks/use-gateway-queries';
import { useAuth } from '../auth';
import {
  useCompleteOnboarding,
  useOnboardingData,
  useOnboardingStatus,
  useSetOnboardingStep,
  useSetUserSettings,
  useSkipOnboarding,
  useUpdateOnboardingData,
  useUserSettings,
} from '../stores/personalization-store';
import { toast } from '../utils/toast';
import { Button } from './button';

export type OnboardingFlowProps = {
  onComplete?: () => void;
  onSkip?: () => void;
};

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete, onSkip }) => {
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const onboardingStatus = useOnboardingStatus();
  const onboardingData = useOnboardingData();
  const completeOnboarding = useCompleteOnboarding();
  const skipOnboarding = useSkipOnboarding();
  const setOnboardingStep = useSetOnboardingStep();
  const updateOnboardingData = useUpdateOnboardingData();
  const setUserSettings = useSetUserSettings();

  const existingUserSettings = useUserSettings();

  const updateUserMutation = useUpdateUserPersonalizationMutation();
  const updateOrgMutation = useUpdateOrgPersonalizationMutation();

  const isAdmin = user?.role === 'admin';
  const hasOrganization = !!user?.organizationId;

  const { data: existingOrgData } = useOrgPersonalizationQuery(hasOrganization);
  const currentStep: OnboardingStep = onboardingStatus.currentStep || 'welcome';
  const [isSaving, setIsSaving] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Pre-populate onboarding data with existing settings (if any)
  React.useEffect(() => {
    if (!isInitialized && (existingUserSettings || existingOrgData)) {
      logger.info('🔄 [ONBOARDING] Pre-populating with existing data');

      const updates: any = {};

      if (existingUserSettings) {
        if (existingUserSettings.displayName)
          updates.displayName = existingUserSettings.displayName;
        if (existingUserSettings.jobTitle) updates.jobTitle = existingUserSettings.jobTitle;
        if (existingUserSettings.customInstructions)
          updates.customInstructions = existingUserSettings.customInstructions;
        if (existingUserSettings.otherDetails)
          updates.userOtherDetails = existingUserSettings.otherDetails;
      }

      if (isAdmin && existingOrgData?.body?.settings) {
        const settings = existingOrgData.body.settings;
        if (settings.organizationDisplayName)
          updates.organizationDisplayName = settings.organizationDisplayName;
        if (settings.primarySpecialty) updates.primarySpecialty = settings.primarySpecialty;
        if (settings.commonCptCodes) updates.commonCptCodes = settings.commonCptCodes;
        if (settings.providerTaxonomies) updates.providerTaxonomies = settings.providerTaxonomies;
        if (settings.otherDetails) updates.orgOtherDetails = settings.otherDetails;
      }

      if (Object.keys(updates).length > 0) {
        updateOnboardingData(updates);
      }

      setIsInitialized(true);
    }
  }, [existingUserSettings, existingOrgData, isAdmin, isInitialized, updateOnboardingData]);

  const handleSkip = React.useCallback(() => {
    skipOnboarding();
    onSkip?.();
  }, [skipOnboarding, onSkip]);

  const handleComplete = React.useCallback(async () => {
    setIsSaving(true);

    try {
      if (
        onboardingData.displayName ||
        onboardingData.jobTitle ||
        onboardingData.customInstructions
      ) {
        const userSettings = {
          displayName: onboardingData.displayName,
          jobTitle: onboardingData.jobTitle,
          customInstructions: onboardingData.customInstructions,
          otherDetails: onboardingData.userOtherDetails,
        };

        await updateUserMutation.mutateAsync({
          body: { settings: userSettings, activeMode: 'org' as const },
        });

        setUserSettings(userSettings);
      }

      if (isAdmin && onboardingData.organizationDisplayName && onboardingData.primarySpecialty) {
        await updateOrgMutation.mutateAsync({
          body: {
            settings: {
              organizationDisplayName: onboardingData.organizationDisplayName,
              primarySpecialty: onboardingData.primarySpecialty,
              commonCptCodes: onboardingData.commonCptCodes || [],
              providerTaxonomies: onboardingData.providerTaxonomies || [],
              otherDetails: onboardingData.orgOtherDetails,
            },
          },
        });
      }

      if (isAdmin) {
        await queryClient.refetchQueries({ queryKey: gatewayQueryKeys.personalization.org });
      }

      completeOnboarding();
      onComplete?.();

      toast.success('Personalization setup complete!');
    } catch (error: any) {
      logger.error('[ONBOARDING] Error saving settings:', error);

      if (error?.status === 401 || error?.body?.error === 'invalid_token_payload') {
        toast.error('Session expired. Please log in again.');
        skipOnboarding();
        await logout();
      } else {
        toast.error('Failed to save settings. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    onboardingData,
    isAdmin,
    updateUserMutation,
    updateOrgMutation,
    completeOnboarding,
    onComplete,
    queryClient,
    skipOnboarding,
    logout,
    setUserSettings,
  ]);

  const handleNext = React.useCallback(
    (nextStep: OnboardingStep) => {
      setOnboardingStep(nextStep);
    },
    [setOnboardingStep]
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5 pointer-events-none" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/15 rounded-full filter blur-3xl animate-float-slow" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-blue-500/15 rounded-full filter blur-3xl animate-float-slower" />
        <div className="absolute top-1/2 right-1/3 w-[300px] h-[300px] bg-purple-500/10 rounded-full filter blur-3xl animate-float-medium" />
        <div className="absolute bottom-1/4 left-1/3 w-[350px] h-[350px] bg-cyan-500/10 rounded-full filter blur-3xl animate-float-medium-delay" />
        <div className="absolute top-1/3 left-1/2 w-[200px] h-[200px] bg-primary/20 rounded-full filter blur-2xl animate-float-fast" />
      </div>

      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-primary/3 to-transparent animate-gradient-shift pointer-events-none" />

      <style>{`
 @keyframes float-slow {
 0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.15; }
 33% { transform: translate(30px, -30px) scale(1.1); opacity: 0.20; }
 66% { transform: translate(-20px, 20px) scale(0.9); opacity: 0.12; }
 }
 @keyframes float-slower {
 0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.15; }
 33% { transform: translate(-40px, 30px) scale(0.95); opacity: 0.18; }
 66% { transform: translate(25px, -25px) scale(1.05); opacity: 0.13; }
 }
 @keyframes float-medium {
 0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.10; }
 50% { transform: translate(20px, 20px) scale(1.15); opacity: 0.15; }
 }
 @keyframes float-medium-delay {
 0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.10; }
 50% { transform: translate(-25px, -15px) scale(1.1); opacity: 0.14; }
 }
 @keyframes float-fast {
 0%, 100% { transform: translate(0, 0); opacity: 0.20; }
 50% { transform: translate(15px, -15px); opacity: 0.25; }
 }
 @keyframes gradient-shift {
 0%, 100% { opacity: 1; }
 50% { opacity: 0.5; }
 }
 .animate-float-slow { animation: float-slow 20s ease-in-out infinite; }
 .animate-float-slower { animation: float-slower 25s ease-in-out infinite; }
 .animate-float-medium { animation: float-medium 15s ease-in-out infinite; }
 .animate-float-medium-delay { animation: float-medium-delay 18s ease-in-out infinite 2s; }
 .animate-float-fast { animation: float-fast 10s ease-in-out infinite; }
 .animate-gradient-shift { animation: gradient-shift 8s ease-in-out infinite; }
 `}</style>

      {/* Skip button */}
      <div className="absolute top-6 right-6 z-10">
        <Button variant="ghost" onClick={handleSkip} className="gap-2">
          <X className="w-4 h-4" />
          Skip for now
        </Button>
      </div>

      {/* Main content */}
      <div className="relative flex-1 overflow-x-hidden overflow-y-auto">
        <div className="w-full max-w-3xl mx-auto px-8 py-16">
          {currentStep === 'welcome' && (
            <WelcomeStep onNext={() => handleNext('user-info')} user={user} />
          )}

          {currentStep === 'user-info' && (
            <UserInfoStep
              onNext={() => handleNext(isAdmin && hasOrganization ? 'org-setup' : 'confirmation')}
              onBack={() => handleNext('welcome')}
              data={onboardingData}
              updateData={updateOnboardingData}
            />
          )}

          {currentStep === 'org-setup' && isAdmin && hasOrganization && (
            <OrgSetupStep
              onNext={() => handleNext('confirmation')}
              onBack={() => handleNext('user-info')}
              data={onboardingData}
              updateData={updateOnboardingData}
            />
          )}

          {currentStep === 'confirmation' && (
            <ConfirmationStep
              onConfirm={handleComplete}
              onBack={() => handleNext(isAdmin && hasOrganization ? 'org-setup' : 'user-info')}
              data={onboardingData}
              isAdmin={isAdmin}
              isSaving={isSaving}
            />
          )}
        </div>
      </div>

      {/* Progress indicator */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
        <div className="flex items-center gap-2 px-4 py-2 glass-card-bg glass-card-border rounded-full">
          {['welcome', 'user-info', isAdmin && hasOrganization && 'org-setup', 'confirmation']
            .filter((s): s is string => Boolean(s))
            .map(step => (
              <div
                key={step}
                className={`h-2 rounded-full transition-all duration-300 ${
                  step === currentStep ? 'w-8 bg-primary shadow-sm' : 'w-2 bg-muted-foreground/30'
                }`}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

const WelcomeStep: React.FC<{ onNext: () => void; user: any }> = ({ onNext, user }) => (
  <div className="flex flex-col items-center gap-8 text-center animate-fade-in">
    <div className="p-6 glass-card-bg glass-card-border rounded-full">
      <Sparkles className="w-16 h-16 text-primary animate-pulse" />
    </div>

    <div className="flex flex-col gap-4">
      <h1 className="text-4xl font-bold text-foreground">Welcome to Enter Control!</h1>
      <p className="text-xl text-muted-foreground max-w-2xl">
        Let's personalize your AI assistant to better understand your role and help you work more
        efficiently.
      </p>
      <div className="inline-flex items-center justify-center gap-2 px-4 py-2 glass-card-bg glass-card-border rounded-full mx-auto">
        <span className="text-sm text-muted-foreground">Hi</span>
        <span className="font-medium text-foreground">{user?.name || user?.email}</span>
        <span className="text-sm text-muted-foreground">• This will only take a minute</span>
      </div>
    </div>

    <Button onClick={onNext} variant="primary" size="lg" className="mt-4 gap-2 rounded-xl">
      Let's Get Started
      <Sparkles className="w-4 h-4" />
    </Button>
  </div>
);

const inputClass =
  'px-4 py-3 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 focus:bg-background transition-all duration-200';

const UserInfoStep: React.FC<{
  onNext: () => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}> = ({ onNext, onBack, data, updateData }) => (
  <div className="flex flex-col gap-6 animate-fade-in">
    <div className="flex items-center gap-4 mb-2">
      <div className="p-3 glass-card-bg glass-card-border rounded-xl">
        <User className="w-6 h-6 text-primary" />
      </div>
      <h2 className="text-2xl font-bold text-foreground">Tell us about yourself</h2>
    </div>

    <div className="flex flex-col gap-5 p-6 glass-card-bg glass-card-border rounded-2xl">
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-foreground">What's your name?</label>
        <input
          type="text"
          placeholder="e.g., John Smith"
          value={data.displayName || ''}
          onChange={e => updateData({ displayName: e.target.value })}
          className={inputClass}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-foreground">
          What's your role or job title?
        </label>
        <input
          type="text"
          placeholder="e.g., RCM Specialist, Billing Manager"
          value={data.jobTitle || ''}
          onChange={e => updateData({ jobTitle: e.target.value })}
          className={inputClass}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-foreground">
          Any specific instructions for how the AI should assist you? (Optional)
        </label>
        <textarea
          placeholder="e.g., Always be concise, Focus on billing accuracy..."
          value={data.customInstructions || ''}
          onChange={e => updateData({ customInstructions: e.target.value })}
          rows={3}
          className={`${inputClass} resize-vertical`}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-foreground">Other Details (Optional)</label>
        <textarea
          placeholder="Any additional information about yourself..."
          value={data.userOtherDetails || ''}
          onChange={e => updateData({ userOtherDetails: e.target.value })}
          rows={3}
          className={`${inputClass} resize-vertical`}
        />
      </div>
    </div>

    <div className="flex gap-3 mt-4">
      <Button variant="outline" onClick={onBack} className="gap-2">
        <ArrowLeft className="w-4 h-4" />
        Back
      </Button>
      <Button
        onClick={onNext}
        disabled={!data.displayName || !data.jobTitle}
        variant="primary"
        className="flex-1 gap-2"
      >
        Continue
        <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

const OrgSetupStep: React.FC<{
  onNext: () => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}> = ({ onNext, onBack, data, updateData }) => (
  <div className="flex flex-col gap-6 animate-fade-in">
    <div className="flex items-center gap-4 mb-2">
      <div className="p-3 glass-card-bg glass-card-border rounded-xl">
        <Building2 className="w-6 h-6 text-primary" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-foreground">Set up your organization</h2>
        <p className="text-sm text-muted-foreground">
          These defaults will apply to all users in your organization
        </p>
      </div>
    </div>

    <div className="flex flex-col gap-5 p-6 glass-card-bg glass-card-border rounded-2xl">
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-foreground">Organization Name*</label>
        <input
          type="text"
          placeholder="e.g., ABC Medical Group"
          value={data.organizationDisplayName || ''}
          onChange={e => updateData({ organizationDisplayName: e.target.value })}
          className={inputClass}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-foreground">Primary Medical Specialty*</label>
        <input
          type="text"
          placeholder="e.g., Gastroenterology, Family Medicine"
          value={data.primarySpecialty || ''}
          onChange={e => updateData({ primarySpecialty: e.target.value })}
          className={inputClass}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-foreground">Other Details (Optional)</label>
        <textarea
          placeholder="Any additional information about your organization..."
          value={data.orgOtherDetails || ''}
          onChange={e => updateData({ orgOtherDetails: e.target.value })}
          rows={3}
          className={`${inputClass} resize-vertical`}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        You can add CPT codes and taxonomies later in Settings
      </p>
    </div>

    <div className="flex gap-3 mt-4">
      <Button variant="outline" onClick={onBack} className="gap-2">
        <ArrowLeft className="w-4 h-4" />
        Back
      </Button>
      <Button
        onClick={onNext}
        disabled={!data.organizationDisplayName || !data.primarySpecialty}
        variant="primary"
        className="flex-1 gap-2"
      >
        Continue
        <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

const ConfirmationStep: React.FC<{
  onConfirm: () => void;
  onBack: () => void;
  data: any;
  isAdmin: boolean;
  isSaving?: boolean;
}> = ({ onConfirm, onBack, data, isAdmin, isSaving = false }) => (
  <div className="flex flex-col gap-6 animate-fade-in">
    <div className="flex items-center gap-4 mb-2">
      <div className="p-3 glass-card-bg glass-card-border rounded-xl">
        <Sparkles className="w-6 h-6 text-primary" />
      </div>
      <h2 className="text-2xl font-bold text-foreground">Looking good!</h2>
    </div>

    <div className="flex flex-col gap-4">
      <div className="p-6 glass-card-bg glass-card-border rounded-2xl">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-foreground">
          <User className="w-4 h-4 text-primary" />
          Your Information
        </h3>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/20 dark:border-border">
            <span className="text-sm text-muted-foreground min-w-[100px]">Name</span>
            <span className="text-sm font-medium text-foreground">{data.displayName}</span>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/20 dark:border-border">
            <span className="text-sm text-muted-foreground min-w-[100px]">Role</span>
            <span className="text-sm font-medium text-foreground">{data.jobTitle}</span>
          </div>
          {data.customInstructions && (
            <div className="flex flex-col gap-1.5 p-3 rounded-xl bg-muted/30 border border-border/20 dark:border-border">
              <span className="text-sm text-muted-foreground">Custom Instructions</span>
              <span className="text-sm font-medium text-foreground">{data.customInstructions}</span>
            </div>
          )}
          {data.userOtherDetails && (
            <div className="flex flex-col gap-1.5 p-3 rounded-xl bg-muted/30 border border-border/20 dark:border-border">
              <span className="text-sm text-muted-foreground">Other Details</span>
              <span className="text-sm font-medium text-foreground">{data.userOtherDetails}</span>
            </div>
          )}
        </div>
      </div>

      {isAdmin && data.organizationDisplayName && (
        <div className="p-6 glass-card-bg glass-card-border rounded-2xl">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-foreground">
            <Building2 className="w-4 h-4 text-primary" />
            Organization Defaults
          </h3>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/20 dark:border-border">
              <span className="text-sm text-muted-foreground min-w-[100px]">Organization</span>
              <span className="text-sm font-medium text-foreground">
                {data.organizationDisplayName}
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/20 dark:border-border">
              <span className="text-sm text-muted-foreground min-w-[100px]">Specialty</span>
              <span className="text-sm font-medium text-foreground">{data.primarySpecialty}</span>
            </div>
            {data.orgOtherDetails && (
              <div className="flex flex-col gap-1.5 p-3 rounded-xl bg-muted/30 border border-border/20 dark:border-border">
                <span className="text-sm text-muted-foreground">Other Details</span>
                <span className="text-sm font-medium text-foreground">{data.orgOtherDetails}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    <p className="text-sm text-muted-foreground text-center">
      You can always update these settings later in the Personalization tab
    </p>

    <div className="flex gap-3 mt-4">
      <Button variant="outline" onClick={onBack} disabled={isSaving} className="gap-2">
        <ArrowLeft className="w-4 h-4" />
        Back
      </Button>
      <Button onClick={onConfirm} variant="primary" className="flex-1 gap-2" disabled={isSaving}>
        {isSaving ? 'Saving...' : 'Complete Setup'}
        <Sparkles className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

OnboardingFlow.displayName = 'OnboardingFlow';
